import { useCallback, useMemo, useState } from 'react';

import {
    type ReportWidgetInteractionPayload,
    type ReportWidgetItem,
    getChartItemId,
} from '../types';

interface UseComposableWidgetInteractionsParams {
    widgetId: string;
    chartItems: ReportWidgetItem[];
    isSideBySide: boolean;
    onRowClick?: (payload: ReportWidgetInteractionPayload) => void;
    onChartItemClick?: (payload: ReportWidgetInteractionPayload) => void;
    onDetailsClick?: (payload: ReportWidgetInteractionPayload) => void;
}

interface UseComposableWidgetInteractionsResult {
    hoveredItemId: string | null;
    tableActiveItemId: string | undefined;
    handleRowClick: (item: ReportWidgetItem) => void;
    handleChartItemClick: (item: ReportWidgetItem) => void;
    handleDetailsClick: (item: ReportWidgetItem) => void;
    handleChartItemHover: (item: ReportWidgetItem | null) => void;
    handleTableRowHover: ((item: ReportWidgetItem | null) => void) | undefined;
    handleLoadMoreHover: ((hovering: boolean) => void) | undefined;
}

function buildPayload(
    widgetId: string,
    interactionType: ReportWidgetInteractionPayload['interactionType'],
    item: ReportWidgetItem,
): ReportWidgetInteractionPayload {
    return {
        widgetId,
        interactionType,
        itemId: item.id,
        itemLabel: item.label,
        item,
        meta: item.meta,
    };
}

export const useComposableWidgetInteractions = ({
    widgetId,
    chartItems,
    isSideBySide,
    onRowClick,
    onChartItemClick,
    onDetailsClick,
}: UseComposableWidgetInteractionsParams): UseComposableWidgetInteractionsResult => {
    const [hoveredItemId, setHoveredItemId] = useState<string | null>(null);

    const aggregatedOtherChartItemId = useMemo(
        () =>
            chartItems.find((item) => item.meta?.isAggregatedOther === true)
                ?.id,
        [chartItems],
    );

    const tableActiveItemId =
        hoveredItemId &&
        hoveredItemId !== aggregatedOtherChartItemId &&
        isSideBySide
            ? hoveredItemId
            : undefined;

    const handleRowClick = useCallback(
        (item: ReportWidgetItem) => {
            onRowClick?.(buildPayload(widgetId, 'row_click', item));
        },
        [onRowClick, widgetId],
    );

    const handleChartItemClick = useCallback(
        (item: ReportWidgetItem) => {
            onChartItemClick?.(
                buildPayload(widgetId, 'chart_item_click', item),
            );
        },
        [onChartItemClick, widgetId],
    );

    const handleDetailsClick = useCallback(
        (item: ReportWidgetItem) => {
            onDetailsClick?.(buildPayload(widgetId, 'details_click', item));
        },
        [onDetailsClick, widgetId],
    );

    const handleChartItemHover = useCallback(
        (item: ReportWidgetItem | null) => {
            setHoveredItemId(item ? getChartItemId(item) : null);
        },
        [],
    );

    const handleTableRowHover = useMemo(() => {
        if (!isSideBySide) {
            return undefined;
        }

        return (item: ReportWidgetItem | null) => {
            setHoveredItemId(item ? getChartItemId(item) : null);
        };
    }, [isSideBySide]);

    const handleLoadMoreHover = useMemo(() => {
        if (!aggregatedOtherChartItemId) {
            return undefined;
        }

        return (hovering: boolean) => {
            setHoveredItemId(hovering ? aggregatedOtherChartItemId : null);
        };
    }, [aggregatedOtherChartItemId]);

    return {
        hoveredItemId,
        tableActiveItemId,
        handleRowClick,
        handleChartItemClick,
        handleDetailsClick,
        handleChartItemHover,
        handleTableRowHover,
        handleLoadMoreHover,
    };
};
